/**
 * DTO du contenu public (DECISIONS §4.2 — section Contenu).
 *
 * **Règle d'exposition (F13, opposable)** : une carte non possédée est exposée avec son
 * `nameKey` mais **sans** `descKey`, **sans** `effects` et **sans** `tags` (`null`).
 * L'effet d'une carte verrouillée ne doit jamais transiter par le réseau avant son acquisition.
 *
 * Les valeurs d'équilibrage nécessaires à la simulation locale (dégâts, portées, PV, vitesses)
 * sont, elles, exposées : la sécurité ne repose pas sur leur secret mais sur la validation
 * serveur (DECISIONS §8.7).
 */
import { z } from 'zod';
import { ameliorationPermanenteSchema, balanceSchema, cardEffectSchema, elementDefinitionSchema, ennemiSchema, modeSchema, niveauSchema, rareteSchema, tourSchema, } from '../content/index.js';
/** Élément d'une carte : un des quatre, ou `neutre`. */
export const elementCarteSchema = z.union([
    z.enum(['feu', 'eau', 'terre', 'air']),
    z.literal('neutre'),
]);
/**
 * Carte telle qu'exposée au client.
 *
 * Nommage aligné sur DECISIONS §4.2 (`rarity`, `effect`), qui fait foi sur le contrat réseau ;
 * les structures internes de contenu, elles, utilisent `rarete` / `effets` (DECISIONS §6.4).
 */
export const cartePubliqueSchema = z
    .object({
    id: z.string(),
    element: elementCarteSchema,
    rarity: rareteSchema,
    nameKey: z.string(),
    /** Masqué (`null`) tant que la carte n'est pas possédée. */
    descKey: z.string().nullable(),
    /** Masqués (`null`) tant que la carte n'est pas possédée. */
    effects: z.array(cardEffectSchema).nullable(),
    /** Étiquettes de synergie : masquées elles aussi (elles trahiraient l'effet). */
    tags: z.array(z.string()).nullable(),
    /** Points de collection de la carte (barème de rareté). */
    points: z.number().int().nonnegative(),
})
    .strict();
/** Niveau tel qu'exposé dans le manifeste (vue légère). */
export const niveauManifesteSchema = z
    .object({
    id: z.string(),
    numero: z.number().int().positive(),
    mode: modeSchema,
    da: z.enum(['feu', 'eau', 'terre', 'air']),
})
    .strict();
/** Réponse de `GET /content/manifest` (`200`). */
export const manifestSchema = z
    .object({
    /** Version de contenu publiée (hash). Toute run y est rattachée. */
    contentVersion: z.string().min(1),
    /** Hash de intégrité du bundle (identique à `contentVersion` si celui-ci est le hash). */
    hash: z.string().min(1),
    elements: z.array(elementDefinitionSchema),
    tours: z.array(tourSchema),
    /**
     * Ennemis du contenu (DECISIONS §1.3 A-2). Le client simule localement avec ces valeurs :
     * leur publicité n'est pas une faille, la validation serveur fait foi (DECISIONS §8.7).
     */
    ennemis: z.array(ennemiSchema),
    /**
     * Catalogue des améliorations permanentes (F18). **Vide** tant que M2-06 n'a pas livré
     * `ameliorations.json` : le champ existe toujours, sa présence ne dépend pas du contenu.
     */
    ameliorations: z.array(ameliorationPermanenteSchema),
    cards: z.array(cartePubliqueSchema),
    levels: z.array(niveauManifesteSchema),
    balance: balanceSchema,
})
    .strict();
/** Réponse de `GET /content/levels/:id` (`200`). */
export const levelResponseSchema = z.object({ level: niveauSchema }).strict();
/** Réponse de `GET /content/cards/:id` (`200`). */
export const cardResponseSchema = z.object({ card: cartePubliqueSchema }).strict();
//# sourceMappingURL=content.js.map